/**
 * Gestione lingua basata sull'URL (SEO-friendly, zero regressioni).
 *
 * Strategia: sottocartelle di lingua come PREFISSO di percorso.
 *   - Italiano (default): nessun prefisso  ->  /esperienze
 *   - Inglese:            prefisso /en      ->  /en/esperienze
 *   - Spagnolo:           prefisso /es      ->  /es/esperienze
 *
 * Il prefisso viene usato come `basename` di React Router: tutte le rotte e i
 * <Link to="/..."> esistenti restano invariati e il prefisso viene mantenuto
 * automaticamente durante la navigazione SPA. Il default (IT) è identico a prima.
 */

export const URL_LOCALES = ['en', 'es'];        // IT è implicito (nessun prefisso)
export const ALL_LOCALES = ['it', 'en', 'es'];

/** Ricava { lang, basename } dal pathname corrente. */
export function detectLocale(pathname) {
  const p = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const seg = p.split('/').filter(Boolean)[0];
  if (URL_LOCALES.includes(seg)) {
    return { lang: seg, basename: `/${seg}` };
  }
  return { lang: 'it', basename: '/' };
}

/** Percorso "logico" senza prefisso lingua (per costruire gli alternate hreflang). */
export function stripLocale(pathname) {
  const p = pathname || '/';
  const parts = p.split('/');
  if (URL_LOCALES.includes(parts[1])) {
    const rest = '/' + parts.slice(2).join('/');
    return rest.replace(/\/+$/, '') || '/';
  }
  return p.replace(/\/+$/, '') || '/';
}

/** Costruisce l'URL completo (path) per una lingua dato un percorso logico senza prefisso. */
export function localizedPath(logicalPath, lang) {
  const clean = ('/' + String(logicalPath || '/').replace(/^\/+/, '')).replace(/\/+$/, '') || '/';
  if (lang === 'it') return clean;
  return `/${lang}${clean === '/' ? '' : clean}`;
}

/**
 * Cambia lingua navigando all'URL localizzato con reload completo:
 * così React Router riparte con il `basename` corretto (/en, /es o /). Preserva
 * query string e hash. È l'unico modo SEO-corretto per cambiare sottocartella lingua.
 */
export function goToLanguage(lang) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem('sila_lang', lang); } catch { /* noop */ }
  // Preferenza ESPLICITA dell'utente: NON viene sovrascritta dalla sync URL→lingua di
  // App.js. La guardia (lib/localeGuard) la usa per mantenere la lingua scelta in tutta
  // l'app (CRM incluso) anche dopo atterraggi senza prefisso (es. login Google).
  try { localStorage.setItem('sila_lang_pref', lang); } catch { /* noop */ }
  const logical = stripLocale(window.location.pathname);
  const target = localizedPath(logical, lang) + (window.location.search || '') + (window.location.hash || '');
  window.location.assign(target);
}

/** Lingua attiva ricavata dall'URL corrente (fonte di verità). */
export function currentLang() {
  return detectLocale(typeof window !== 'undefined' ? window.location.pathname : '/').lang;
}

/**
 * Prefissa un percorso logico con la lingua ATTIVA (da URL).
 * Utile per i redirect "hard" (window.location) che altrimenti perderebbero il
 * prefisso /es|/en e farebbero scattare un secondo redirect della guardia (flash bianco).
 */
export function withCurrentLocale(logicalPath) {
  return localizedPath(logicalPath, currentLang());
}

